"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import { useMatch } from "fansunited-data-layer/client";
import { SectionHeader, EntityDisplay } from "fansunited-sports-ui";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { TeamLineup } from "./TeamLineup";
import { SubstitutionsSection } from "./SubstitutionsSection";
import { computePlayerEvents, extractSubstitutions } from "./playerEventsHelper";
interface MatchLineupsProps {
  homeTeamName: string;
  awayTeamName: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  homeTeamCountryCode?: string;
  homeTeamCountryFlag?: string;
  homeTeamPrimaryColor?: string;
  homeTeamSecondaryColor?: string;
  awayTeamCountryCode?: string;
  awayTeamCountryFlag?: string;
  awayTeamPrimaryColor?: string;
  awayTeamSecondaryColor?: string;
  /** For mobile controlled mode - which team to show (undefined = show both) */
  activeTeam?: "home" | "away";
}

/**
 * MatchLineups - Displays team lineups with formations
 * When activeTeam is provided, shows only that team's lineup (mobile mode)
 * Otherwise shows both teams side by side (desktop mode)
 */
export const MatchLineups: React.FC<MatchLineupsProps> = t0 => {
  const $ = _c(75);
  const {
    homeTeamName,
    awayTeamName,
    homeTeamLogo,
    awayTeamLogo,
    homeTeamCountryCode,
    homeTeamCountryFlag,
    homeTeamPrimaryColor,
    homeTeamSecondaryColor,
    awayTeamCountryCode,
    awayTeamCountryFlag,
    awayTeamPrimaryColor,
    awayTeamSecondaryColor,
    activeTeam
  } = t0;
  const {
    lineups,
    events
  } = useMatch();
  const labels = useLabels();
  let t1;
  if ($[0] !== events) {
    t1 = events || [];
    $[0] = events;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = computePlayerEvents(t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const playerEventsMap = t2;
  let t3;
  if ($[4] !== events) {
    t3 = events || [];
    $[4] = events;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = extractSubstitutions(t3);
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const substitutions = t4;
  if (!lineups || !lineups.confirmed) {
    return null;
  }
  const homeLineup = lineups.competitorOne;
  const awayLineup = lineups.competitorTwo;
  if (activeTeam) {
    const isHome = activeTeam === "home";
    const lineup = isHome ? homeLineup : awayLineup;
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <SectionHeader entity={{
        name: "Lineups",
        styles: {
          typography: {
            name: "text-sm uppercase font-semibold text-foreground"
          }
        }
      }} styles={{
        container: "mb-2"
      }} />;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== labels || $[10] !== lineup.starters || $[11] !== lineup.substitutes || $[12] !== playerEventsMap) {
      t6 = <TeamLineup starters={lineup.starters} substitutes={lineup.substitutes} isHome={true} labels={labels} playerEventsMap={playerEventsMap} />;
      $[9] = labels;
      $[10] = lineup.starters;
      $[11] = lineup.substitutes;
      $[12] = playerEventsMap;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== activeTeam || $[15] !== labels || $[16] !== substitutions.away || $[17] !== substitutions.home) {
      t7 = <SubstitutionsSection homeSubstitutions={substitutions.home} awaySubstitutions={substitutions.away} labels={labels} activeTeam={activeTeam} />;
      $[14] = activeTeam;
      $[15] = labels;
      $[16] = substitutions.away;
      $[17] = substitutions.home;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== t6 || $[20] !== t7) {
      t8 = <div>{t5}<div className="bg-card rounded-md shadow-md overflow-hidden border border-border">{t6}{t7}</div></div>;
      $[19] = t6;
      $[20] = t7;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    return t8;
  }
  let t5;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <SectionHeader entity={{
      name: "Lineups",
      styles: {
        typography: {
          name: "text-sm uppercase font-semibold text-foreground"
        }
      }
    }} styles={{
      container: "mb-2"
    }} />;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const t6 = homeLineup.coach?.name && `Coach: ${homeLineup.coach.name}`;
  let t7;
  if ($[23] !== homeLineup.formation || $[24] !== t6) {
    t7 = [homeLineup.formation, t6].filter(Boolean);
    $[23] = homeLineup.formation;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const t8 = t7.join(" \xB7 ");
  let t9;
  if ($[26] !== homeTeamCountryCode || $[27] !== homeTeamCountryFlag || $[28] !== homeTeamLogo || $[29] !== homeTeamPrimaryColor || $[30] !== homeTeamSecondaryColor) {
    t9 = <TeamLogo src={homeTeamLogo} size="md" countryCode={homeTeamCountryCode} countryFlag={homeTeamCountryFlag} primaryColor={homeTeamPrimaryColor} secondaryColor={homeTeamSecondaryColor} />;
    $[26] = homeTeamCountryCode;
    $[27] = homeTeamCountryFlag;
    $[28] = homeTeamLogo;
    $[29] = homeTeamPrimaryColor;
    $[30] = homeTeamSecondaryColor;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      imageSize: "md",
      textAlign: "left",
      typography: {
        name: "text-sm font-semibold text-foreground truncate",
        subtitle: "text-xs text-muted-foreground"
      }
    };
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== homeTeamName || $[34] !== t8 || $[35] !== t9) {
    t11 = <div className="px-3 py-3 flex justify-start"><EntityDisplay name={homeTeamName} subtitle={t8} image={t9} imagePosition="left" styles={t10} /></div>;
    $[33] = homeTeamName;
    $[34] = t8;
    $[35] = t9;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const t12 = awayLineup.coach?.name && `Coach: ${awayLineup.coach.name}`;
  let t13;
  if ($[37] !== awayLineup.formation || $[38] !== t12) {
    t13 = [awayLineup.formation, t12].filter(Boolean);
    $[37] = awayLineup.formation;
    $[38] = t12;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  const t14 = t13.join(" \xB7 ");
  let t15;
  if ($[40] !== awayTeamCountryCode || $[41] !== awayTeamCountryFlag || $[42] !== awayTeamLogo || $[43] !== awayTeamPrimaryColor || $[44] !== awayTeamSecondaryColor) {
    t15 = <TeamLogo src={awayTeamLogo} size="md" countryCode={awayTeamCountryCode} countryFlag={awayTeamCountryFlag} primaryColor={awayTeamPrimaryColor} secondaryColor={awayTeamSecondaryColor} />;
    $[40] = awayTeamCountryCode;
    $[41] = awayTeamCountryFlag;
    $[42] = awayTeamLogo;
    $[43] = awayTeamPrimaryColor;
    $[44] = awayTeamSecondaryColor;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      imageSize: "md",
      textAlign: "right",
      typography: {
        name: "text-sm font-semibold text-foreground truncate",
        subtitle: "text-xs text-muted-foreground"
      }
    };
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== awayTeamName || $[48] !== t14 || $[49] !== t15) {
    t17 = <div className="px-3 py-3 flex justify-end"><EntityDisplay name={awayTeamName} subtitle={t14} image={t15} imagePosition="right" styles={t16} /></div>;
    $[47] = awayTeamName;
    $[48] = t14;
    $[49] = t15;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== t11 || $[52] !== t17) {
    t18 = <div className="grid grid-cols-2 bg-muted border-b border-border">{t11}{t17}</div>;
    $[51] = t11;
    $[52] = t17;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== homeLineup.starters || $[55] !== homeLineup.substitutes || $[56] !== labels || $[57] !== playerEventsMap) {
    t19 = <TeamLineup starters={homeLineup.starters} substitutes={homeLineup.substitutes} isHome={true} labels={labels} playerEventsMap={playerEventsMap} />;
    $[54] = homeLineup.starters;
    $[55] = homeLineup.substitutes;
    $[56] = labels;
    $[57] = playerEventsMap;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] !== awayLineup.starters || $[60] !== awayLineup.substitutes || $[61] !== labels || $[62] !== playerEventsMap) {
    t20 = <TeamLineup starters={awayLineup.starters} substitutes={awayLineup.substitutes} isHome={false} labels={labels} playerEventsMap={playerEventsMap} />;
    $[59] = awayLineup.starters;
    $[60] = awayLineup.substitutes;
    $[61] = labels;
    $[62] = playerEventsMap;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  let t21;
  if ($[64] !== t19 || $[65] !== t20) {
    t21 = <div className="grid grid-cols-2 divide-x divide-border">{t19}{t20}</div>;
    $[64] = t19;
    $[65] = t20;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  let t22;
  if ($[67] !== labels || $[68] !== substitutions.away || $[69] !== substitutions.home) {
    t22 = <SubstitutionsSection homeSubstitutions={substitutions.home} awaySubstitutions={substitutions.away} labels={labels} />;
    $[67] = labels;
    $[68] = substitutions.away;
    $[69] = substitutions.home;
    $[70] = t22;
  } else {
    t22 = $[70];
  }
  let t23;
  if ($[71] !== t18 || $[72] !== t21 || $[73] !== t22) {
    t23 = <div>{t5}<div className="bg-card rounded-md shadow-md overflow-hidden border border-border">{t18}{t21}{t22}</div></div>;
    $[71] = t18;
    $[72] = t21;
    $[73] = t22;
    $[74] = t23;
  } else {
    t23 = $[74];
  }
  return t23;
};